import {
  takeLatest,
  take,
  put,
  call,
  select,
  delay,
  fork,
  cancel,
} from "redux-saga/effects";
import ActionTypes from "./actionTypes";
import {
  requestAddItemFailure,
  requestAddItemSuccessful,
  requestCreateCartFailure,
  requestCreateCartSuccessful,
  requestCreateOrderFromCartFailure,
  requestCreateOrderFromCartSuccessful,
  requestDeleteItemFailure,
  requestDeleteItemSuccessful,
  requestLoadCartFailure,
  requestLoadCartSuccessful,
  requestLoadOrderFailure,
  requestLoadOrderSuccessful,
  requestPaymentFailure,
  requestPaymentSuccessful,
  requestUpdateItemFailure,
  requestUpdateItemSuccessful,
  clearCart,
  requestCartValidationSuccessful,
  requestCartValidationFailure,
  setLoading,
  setCart,
  requestOrdersListSuccessful,
  requestOrdersListFailure,
  requestCreateOrderReviewFailure,
  requestCreateOrderReviewSuccessful,
  requestSetPaymentOptionSuccessful,
  requestSetPaymentOptionFailure,
  requestSetRewardSuccessful,
  requestSetRewardFailure,
  requestSetScheduleSuccessful,
  requestSetScheduleFailure,
  requestSetPromoCodesSuccessful,
  requestSetPromoCodesFailure,
} from "./actions";
import ECommerceConfig from "../config";
import {cartSelector} from "./selectors";
import get from "lodash/get";
import {getError} from "src/core/common/utils";
import localStorage from "src/core/common/localStorage";

function* createCart(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const {cart} = action.payload;
    const newCart = yield call(backend.createCart, cart);
    yield put(requestCreateCartSuccessful(newCart));
  } catch (error) {
    yield put(requestCreateCartFailure(error));
  }
}
export function* createCartSaga() {
  yield takeLatest(ActionTypes.REQUEST_CREATE_CART, createCart);
}

function* addItem(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const {item} = action.payload;
    let {cart} = action.payload;
    let newCart;
    if (!cart) {
      newCart = yield call(backend.createCart, item);
    } else {
      newCart = yield call(backend.addItem, cart, item);
    }
    const cartWithPrices = yield call(tryCartValidation, newCart);
    yield put(requestAddItemSuccessful(cartWithPrices || newCart, item));
  } catch (error) {
    yield put(requestAddItemFailure(error));
  }
}
export function* addItemSaga() {
  yield takeLatest(ActionTypes.REQUEST_ADD_ITEM, addItem);
}

function* deleteItem(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const {cart, item} = action.payload;
    const newCart = yield call(backend.deleteItem, cart, item);

    if (!newCart || newCart.getItems().length === 0) {
      yield put(clearCart());
    } else {
      yield put(requestDeleteItemSuccessful(newCart, item));
    }
  } catch (error) {
    yield put(requestDeleteItemFailure(error));
  }
}
export function* deleteItemSaga() {
  yield takeLatest(ActionTypes.REQUEST_DELETE_ITEM, deleteItem);
}

function* updateItem(action) {
  const backend = ECommerceConfig.getBackend();
  const {cart, item} = action.payload;
  try {
    const newCart = yield call(backend.updateItem, cart, item);
    yield put(requestUpdateItemSuccessful(newCart, item, cart));
  } catch (error) {
    yield put(setCart(cart));
    yield put(requestUpdateItemFailure(error));
  }
}
export function* updateItemSaga() {
  yield takeLatest(ActionTypes.REQUEST_UPDATE_ITEM, updateItem);
}

function* pay(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const {order, additionalInfo} = action.payload;
    const newOrder = yield call(backend.pay, order, additionalInfo);
    yield put(requestPaymentSuccessful(newOrder));
  } catch (error) {
    yield put(requestPaymentFailure(error));
  }
}
export function* paymentSaga() {
  yield takeLatest(ActionTypes.REQUEST_PAYMENT, pay);
}

function* loadCart(action) {
  const {cartId} = action.payload;
  const backend = ECommerceConfig.getBackend();
  const hasCart =
    cartId || Object.keys(localStorage.localStorage).some(key => /CART/.test(key));
  if (hasCart) {
    try {
      let cart = yield call(backend.loadCart, cartId);
      yield put(requestLoadCartSuccessful(cart));
    } catch (error) {
      yield put(requestLoadCartFailure(error));
      yield put(clearCart());
    }
  } else {
    yield put(setLoading(false));
  }
}
export function* loadCartSaga() {
  yield takeLatest(ActionTypes.REQUEST_LOAD_CART, loadCart);
}

function* loadOrder(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const {orderId} = action.payload;
    const order = yield call(backend.getOrder, orderId);
    yield put(requestLoadOrderSuccessful(order));
  } catch (error) {
    yield put(requestLoadOrderFailure(error));
  }
}
export function* loadOrderSaga() {
  yield takeLatest(ActionTypes.REQUEST_LOAD_ORDER, loadOrder);
}

function* createOrder(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const {cart, additionalInfo} = action.payload;
    const order = yield call(backend.createOrder, cart, additionalInfo || {});
    yield put(requestCreateOrderFromCartSuccessful(order));
    if (order) {
      yield put(clearCart());
    }
  } catch (error) {
    yield put(requestCreateOrderFromCartFailure(error));
    const cartAlreadySubmittedError = getError("cart_already_submitted", error);
    if (cartAlreadySubmittedError) {
      yield put(clearCart());
    }
  }
}
export function* createOrderSaga() {
  yield takeLatest(ActionTypes.REQUEST_CREATE_ORDER_FROM_CART, createOrder);
}

function* _clearCart() {
  const backend = ECommerceConfig.getBackend();
  yield call(backend.clearCart);
}
export function* clearCartSaga() {
  yield takeLatest(ActionTypes.CLEAR_CART, _clearCart);
}

function* validateCart(action) {
  yield put(setLoading(true));
  yield delay(action.type === ActionTypes.REQUEST_LOAD_CART_SUCCESSFUL ? 500 : 100);
  let cart = yield select(cartSelector);
  if (!cart) return;
  try {
    const backend = ECommerceConfig.getBackend();
    cart = yield call(backend.validateCart, cart, action.payload);
    yield put(requestCartValidationSuccessful(cart));
  } catch (error) {
    yield put(setLoading(false));
    yield handleValidationError(cart, error, requestCartValidationFailure);
  }
}
function* tryCartValidation(cart) {
  const backend = ECommerceConfig.getBackend();
  try {
    const newCart = yield call(backend.validateCart, cart);
    yield put(requestCartValidationSuccessful(newCart));
    return newCart;
  } catch (error) {
    return yield handleValidationError(cart, error, requestCartValidationFailure);
  }
}
function* handleValidationError(currentCart, error, failureAction) {
  const backend = ECommerceConfig.getBackend();
  const _error = yield call(backend.handleValidationErrors, currentCart, error);
  let cart = get(_error, "cart") || currentCart;
  yield put(setCart(cart));
  yield put(failureAction(error));
  return cart;
}

export function* cartValidationSaga() {
  let task;
  while (true) {
    const action = yield take([
      ActionTypes.REQUEST_CART_VALIDATION,
      ActionTypes.REQUEST_SET_PROMO_CODES_SUCCESSFUL,
      ActionTypes.REQUEST_SET_SCHEDULE_SUCCESSFUL,
      ActionTypes.REQUEST_SET_REWARD_SUCCESSFUL,
      ActionTypes.REQUEST_SET_PAYMENT_OPTION_SUCCESSFUL,
      ActionTypes.REQUEST_UPDATE_ITEM_SUCCESSFUL,
      ActionTypes.REQUEST_DELETE_ITEM_SUCCESSFUL,
      ActionTypes.REQUEST_CREATE_CART_SUCCESSFUL,
      ActionTypes.REQUEST_LOAD_CART_SUCCESSFUL,
    ]);

    if (task) {
      yield cancel(task);
    }
    task = yield fork(validateCart, action);
  }
}

function* setPromoCodes(action) {
  const backend = ECommerceConfig.getBackend();
  const {cart, promoCodes} = action.payload;
  try {
    const newCart = yield call(backend.setPromoCodes, cart, promoCodes);
    yield put(requestSetPromoCodesSuccessful(newCart));
  } catch (error) {
    yield put(requestSetPromoCodesFailure(error));
    try {
      const newCart = yield call(backend.setPromoCodes, cart, cart.getPromoCodes());
      yield put(setCart(newCart));
    } catch (error) {
      const newCart = yield call(backend.setPromoCodes, cart, []);
      yield put(setCart(newCart));
    }
    yield put(requestSetPromoCodesFailure(error));
  }
}
export function* setPromoCodesSaga() {
  yield takeLatest(ActionTypes.REQUEST_SET_PROMO_CODES, setPromoCodes);
}

function* setReward(action) {
  const backend = ECommerceConfig.getBackend();
  const {cart, reward} = action.payload;
  try {
    const newCart = yield call(backend.setReward, cart, reward);
    yield put(requestSetRewardSuccessful(newCart));
  } catch (error) {
    yield put(requestSetRewardFailure(error));
    let newCart = yield call(backend.setReward, cart, null);
    newCart = yield call(backend.validateCart, newCart);
    yield put(setCart(newCart));
    yield put(requestSetRewardFailure(error));
  }
}
export function* setRewardSaga() {
  yield takeLatest(ActionTypes.REQUEST_SET_REWARD, setReward);
}

function* setSchedule(action) {
  const backend = ECommerceConfig.getBackend();
  let {cart, schedule} = action.payload;
  if (!cart) {
    cart = yield select(cartSelector);
  }

  if (!cart) {
    yield put(requestSetScheduleSuccessful(null));
  }

  try {
    const newCart = yield call(backend.setSchedule, cart, schedule);
    yield put(setCart(newCart));
    yield put(requestSetScheduleSuccessful(newCart));
  } catch (error) {
    yield put(requestSetScheduleFailure(error));
  }
}
export function* setScheduleSaga() {
  yield takeLatest(ActionTypes.REQUEST_SET_SCHEDULE, setSchedule);
}

function* setPaymentOption(action) {
  const backend = ECommerceConfig.getBackend();
  const {cart, paymentOption} = action.payload;
  try {
    const newCart = yield call(backend.setPaymentOption, cart, paymentOption);
    yield put(requestSetPaymentOptionSuccessful(newCart));
  } catch (error) {
    yield put(requestSetPaymentOptionFailure(error));
  }
}
export function* setPaymentOptionSaga() {
  yield takeLatest(ActionTypes.REQUEST_SET_PAYMENT_OPTION, setPaymentOption);
}

function* loadOrdersList(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const payload = action.payload;
    const orders = yield call(backend.getOrdersList, payload);
    yield put(requestOrdersListSuccessful(orders));
  } catch (error) {
    yield put(requestOrdersListFailure(error));
  }
}
export function* loadOrdersListSaga() {
  yield takeLatest(ActionTypes.REQUEST_ORDERS_LIST, loadOrdersList);
}

function* createOrderReview(action) {
  const backend = ECommerceConfig.getBackend();
  try {
    const payload = action.payload;
    const review = yield call(backend.postOrderReview, payload);
    const order = yield call(backend.getOrder, payload.orderId);
    yield put(requestLoadOrderSuccessful(order));
    yield put(requestCreateOrderReviewSuccessful(review));
  } catch (error) {
    yield put(requestCreateOrderReviewFailure(error));
  }
}
export function* createOrderReviewSaga() {
  yield takeLatest(ActionTypes.REQUEST_CREATE_ORDER_REVIEW, createOrderReview);
}
